// Syntax in this file is a bit weird to shoe horn into both api and frontend
export * from './allocations';
import money from './dinero';
import reprice, {
  changeItineraryDeposit as doChangeItineraryDeposit,
  changeItinerarySell as doChangeItinerarySell,
  changeCurrency as doChangeCurrency,
  DEFAULT_ITRVL_FEE_CREDIT,
  getRSPAmount,
  getRspInTripCurrency,
  determineCustomPriceAdjustment,
  findProblems as doFindProblems,
} from './reprice';
import { getPaymentDiscount as gpd, getWireDiscount as gwd, getItineraryCurrencies as doGetItineraryCurrencies, toDinero } from './util';
import { rateLimits as rateLimitsImport } from './rateLimits';
import { getCurrencyPrecision } from './dinero';
import { SEGMENT_TYPE } from 'itrvl-types';

export const Dinero = money;
export const repriceItinerary = reprice;
export const changeCurrency = doChangeCurrency;
export const getAsDinero = toDinero;
export const DEFAULT_FEE_CREDIT = DEFAULT_ITRVL_FEE_CREDIT;
export const rateLimits = rateLimitsImport;
export const getPaymentDiscount = gpd;
export const getWireDiscount = gwd;
export const changeItinerarySell = doChangeItinerarySell;
export const changeItineraryDeposit = doChangeItineraryDeposit;
export const findProblems = doFindProblems;
export const getItineraryCurrencies = doGetItineraryCurrencies;
export const DEFAULT_CURRENCY = 'USD';

export { getRSPAmount, getRspInTripCurrency, determineCustomPriceAdjustment };

export const fromFormat = (value, currency = 'USD') => {
  const unit = value.replace(/[^\d-.]/g, '');
  try {
    return Dinero({ unit, currency });
  } catch {
    return Dinero({ unit: 0, currency });
  }
};

export const PAYMENT_MODEL = {
  RACK: 'rack',
};

/**
 * Apply a percentage rate to an amount using floating-point arithmetic.
 *
 * NOTE: This function intentionally uses floating-point math instead of Dinero.js
 * to avoid cumulative rounding errors in multi-step currency conversion processes.
 * The precision loss from floating-point operations is less than the value drift
 * that would occur from multiple Dinero rounding operations.
 *
 * @param {number} amount - The base amount to apply the rate to
 * @param {number} rate - The percentage rate (e.g., 20 for 20%)
 * @returns {number} The amount with the rate applied
 */
export const applyRate = (amount, rate) => {
  const ratePercent = rate / 100;
  return (amount / (1 - ratePercent)) * ratePercent + amount;
};

export const rateForType = (type, rates) => {
  switch (type) {
    case SEGMENT_TYPE.STAY:
    case SEGMENT_TYPE.SERVICE:
      return rates.land;
    case SEGMENT_TYPE.PRICE:
      return rates.dmc;
    default:
      return rates.air;
  }
};

export const applyRateForType = (amount, type, rates) => applyRate(amount, rateForType(type, rates));

/**
 * Convert an amount between currencies with precision-aware rounding.
 *
 * @param {number} amount - The non-decimal precision amount to convert eg. USD$1 = 100 (must be a finite number)
 * @param {string} sourceCurrency - The source currency code
 * @param {string} targetCurrency - The target currency code
 * @param {Object} exchange - Exchange rate object with currency codes as keys
 * @returns {number} The converted amount rounded to appropriate precision
 * @throws {Error} When exchange rates are missing or inputs are invalid
 */
export const convertCurrencyWithPrecision = (amount, sourceCurrency, targetCurrency, exchange) => {
  if (typeof amount !== 'number' || !isFinite(amount)) {
    throw new Error(`Invalid amount: ${amount}. Must be a finite number.`);
  }

  if (!sourceCurrency || typeof sourceCurrency !== 'string') {
    throw new Error(`Invalid sourceCurrency: ${sourceCurrency}. Must be a non-empty string.`);
  }

  if (!targetCurrency || typeof targetCurrency !== 'string') {
    throw new Error(`Invalid targetCurrency: ${targetCurrency}. Must be a non-empty string.`);
  }

  if (!exchange || typeof exchange !== 'object') {
    throw new Error(`Invalid exchange: ${exchange}. Must be an object.`);
  }

  if (!exchange[targetCurrency]) {
    throw new Error(`currency ${targetCurrency} not found in exchange: ${JSON.stringify(Object.keys(exchange))}`);
  }

  if (sourceCurrency === targetCurrency) {
    return amount;
  }

  const rate = exchange[targetCurrency];
  if (typeof rate !== 'number' || !isFinite(rate) || rate <= 0) {
    throw new Error(`Invalid exchange rate for ${targetCurrency}: ${rate}. Must be a positive finite number.`);
  }

  const convertedAmount = amount * rate;
  const precisionDiff = getCurrencyPrecision(sourceCurrency) - getCurrencyPrecision(targetCurrency);

  return Math.round(convertedAmount / Math.pow(10, precisionDiff));
};

export default {
  Dinero,
  repriceItinerary,
  changeItinerarySell,
  changeItineraryDeposit,
  getItineraryCurrencies,
  getPaymentDiscount,
  getWireDiscount,
  changeCurrency,
  getAsDinero,
  DEFAULT_FEE_CREDIT,
  rateLimits,
  fromFormat,
  getCurrencyPrecision,
};
